import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { ICommandStore } from "@formant/core/model/data/ICommandStore";
import { IDeviceStore } from "@formant/core/model/data/IDeviceStore";
import { IEventStore } from "@formant/core/model/data/IEventStore";
import { duration } from "@formant/core/model/duration";
import { ICommandProgress } from "@formant/core/model/ICommandProgress";
import { IsoDate } from "@formant/core/model/IsoDate";
import { Uuid } from "@formant/core/model/Uuid";

import { IDeviceConfigurationStore } from "./IDeviceConfigurationStore";
import { IOnDemandRequestEvent, IOnDemandStore } from "./IOnDemandStore";

export class OnDemandStore implements IOnDemandStore {
    constructor(
        private commandStore: ICommandStore,
        private deviceConfigurationStore: IDeviceConfigurationStore,
        private deviceStore: IDeviceStore,
        private eventStore: IEventStore,
    ) {}

    getOnDemandStreamNames(deviceId: Uuid): Set<string> {
        const configuration = this.deviceConfigurationStore.get(deviceId);
        const names =
            (configuration &&
                configuration.document.telemetry?.streams
                    ?.filter(_ => !_.disabled && _.onDemand)
                    .map(_ => _.name)) ||
            [];
        return new Set(names);
    }

    getRecentOnDemandRequests(
        deviceId: Uuid,
        eventQueryStart: IsoDate,
        eventQueryEnd: IsoDate,
    ): IOnDemandRequestEvent[] {
        const results = this.eventStore.query({
            deviceIds: [deviceId],
            start: eventQueryStart,
            end: eventQueryEnd,
            count: 20,
            eventTypes: [
                "command-request",
                "command-response",
                "command-delivery",
            ],
        });
        const device = this.deviceStore.get(deviceId);
        const commandProgress = new Map<string, ICommandProgress>(
            ((device && device.state?.commandProgress) || []).map(_ => [
                _.commandId,
                _,
            ]),
        );
        const requests = new Map<string, IOnDemandRequestEvent>();
        results?.forEach(_ => {
            if (_.type === "command-request") {
                const command = this.commandStore.get(_.commandId);
                if (
                    command &&
                    command.parameter.meta &&
                    command.command === "formant.demand_data"
                ) {
                    const start = new Date(
                        parseFloat(command.parameter.meta.start) *
                            duration.second,
                    );
                    const end = new Date(
                        parseFloat(command.parameter.meta.end) *
                            duration.second,
                    );
                    const streamsText = command.parameter.meta.streams || "";
                    const isAllStreams = streamsText === "";
                    const streams = new Set(
                        isAllStreams
                            ? this.getOnDemandStreamNames(deviceId)
                            : streamsText.split(","),
                    );
                    requests.set(_.commandId, {
                        id: _.commandId,
                        createdTime: parseIsoDate(_.time),
                        finished: false,
                        finishedTime: undefined,
                        received: false,
                        receivedTime: undefined,
                        pending: true,
                        start,
                        end,
                        deviceId,
                        streams,
                        progress: 0,
                    });
                }
            }
        });
        results?.forEach(_ => {
            if (_.type === "command-response") {
                const request = requests.get(_.commandId);
                if (request) {
                    request.pending = false;
                    request.progress = 100;
                    request.finished = true;
                    request.finishedTime = parseIsoDate(_.time);
                }
            }
            if (_.type === "command-delivery") {
                const request = requests.get(_.commandId);
                if (request) {
                    request.received = true;
                    request.receivedTime = parseIsoDate(_.time);
                    if (!request.finished) {
                        // lets make sure we dont overwrite if we get in unexpected order
                        const progress = commandProgress.get(request.id);
                        if (progress) {
                            request.pending = progress.pending;
                            request.progress = progress.progress;
                        }
                    }
                }
            }
        });
        return Array.from(requests.values());
    }
}
